import React, { useState, useEffect } from 'react';
import { FiPlus, FiCheck, FiSend, FiDownload, FiEdit2 } from 'react-icons/fi';
import { api } from '../api';
import { useMe } from '../App';
import { findPerm } from '../perm';
import { exportExcel } from '../utils/exportExcel';

const ST = {
  pending: { background: '#FDF3E3', color: '#A87B2D' },
  ordered: { background: '#E9F0F7', color: '#3A6A96' },
  received: { background: '#E9F3EC', color: '#3B7A4E' },
  cancelled: { background: '#FBEEEA', color: '#B4432E' },
};

export default function PurchaseRequests() {
  const me = useMe();
  const perm = findPerm(me.modules, 'purchase_requests');
  const [rows, setRows] = useState(null);
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(false);
  const [approvalFor, setApprovalFor] = useState(null);
  const [toast, setToast] = useState('');

  async function load() { try { setRows(await api('/requirements/purchase/list')); } catch (e) { setToast(e.message); } }
  useEffect(() => { load(); }, []);
  useEffect(() => { api('/store/options').then(setProducts).catch(() => {}); }, []);
  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2600); }

  async function setStatus(pr, status) {
    try { await api('/requirements/purchase/' + pr.id, { method: 'PATCH', body: { status } });
      flash(status === 'received' ? 'Received — stock added' : 'Updated'); load(); }
    catch (e) { flash(e.message); }
  }

  async function quickCheck(pr) {
    try { await api(`/requirements/purchase/${pr.id}/approval`, { method: 'PATCH', body: { checked: !pr.checked } });
      flash(pr.checked ? 'Unchecked' : 'Checked'); load(); }
    catch (e) { flash(e.message); }
  }

  function fmtDate(d) { return d ? String(d).slice(0, 10) : ''; }

  function doExport() {
    exportExcel('Purchase_requests', [
      { label: 'Product', key: 'product_name' },
      { label: 'Qty', key: 'qty' },
      { label: 'Note', value: (r) => r.note || '' },
      { label: 'Status', key: 'status' },
      { label: 'Raised by', key: 'raiser_name' },
      { label: 'Checked', value: (r) => (r.checked ? 'Yes' : 'No') },
      { label: 'Checked by', value: (r) => r.checked_by_name || '' },
      { label: 'Remarks', value: (r) => r.remarks || '' },
      { label: 'Shared with accounts', value: (r) => (r.shared_with_accounts ? 'Yes' : 'No') },
      { label: 'Shared date', value: (r) => fmtDate(r.shared_date) },
    ], rows || []);
  }

  return (
    <div>
      <div className="mod-head">
        <div className="titles">
          <div className="page-eyebrow">Purchase</div>
          <h1>Purchase Requests</h1>
          <div className="count">{rows ? `${rows.length} request${rows.length !== 1 ? 's' : ''}` : 'Loading…'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm" onClick={doExport} disabled={!rows || rows.length === 0}><FiDownload /> Export to Excel</button>
          {perm.can_create && <button className="btn btn-dark" onClick={() => setModal(true)}><FiPlus /> New request</button>}
        </div>
      </div>

      <div className="table-card">
        {!rows ? <div className="empty">Loading…</div> :
         rows.length === 0 ? <div className="empty"><h3>No purchase requests</h3><p>Raise one when stock runs short.</p></div> :
          <table className="data">
            <thead><tr>
              <th>Product</th><th style={{ textAlign: 'right' }}>Qty</th><th>Status</th><th>Check</th>
              <th>Remarks</th><th>Shared w/ accounts</th><th>Raised by</th>
              {perm.can_edit && <th style={{ textAlign: 'right' }}>Actions</th>}
            </tr></thead>
            <tbody>
              {rows.map((pr) => (
                <tr key={pr.id}>
                  <td style={{ fontWeight: 600 }}>{pr.product_name}{pr.note && <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 400 }}>{pr.note}</div>}</td>
                  <td style={{ textAlign: 'right' }}>{pr.qty}</td>
                  <td><span className="pill" style={ST[pr.status]}>{pr.status}</span></td>
                  <td>
                    {pr.checked
                      ? <span className="pill" style={{ background: '#E9F3EC', color: '#3B7A4E' }} title={pr.checked_by_name ? 'by ' + pr.checked_by_name : ''}><FiCheck style={{ verticalAlign: '-2px' }} /> checked</span>
                      : (perm.can_edit ? <button className="btn btn-sm" onClick={() => quickCheck(pr)}><FiCheck /> Check</button> : <span style={{ color: 'var(--muted)' }}>—</span>)}
                  </td>
                  <td style={{ color: 'var(--muted)', maxWidth: 200, fontSize: 13 }}>{pr.remarks || '—'}</td>
                  <td>
                    {pr.shared_with_accounts
                      ? <span className="pill" style={{ background: '#E9F0F7', color: '#3A6A96' }}><FiSend style={{ verticalAlign: '-2px' }} /> {fmtDate(pr.shared_date) || 'shared'}</span>
                      : <span style={{ color: 'var(--muted)' }}>not shared</span>}
                  </td>
                  <td style={{ color: 'var(--muted)' }}>{pr.raiser_name}</td>
                  {perm.can_edit && (
                    <td style={{ textAlign: 'right' }}>
                      <div className="row-actions">
                        <button className="btn btn-sm" onClick={() => setApprovalFor(pr)} title="Check / remarks / shared"><FiEdit2 /> Review</button>
                        {pr.status !== 'received' && pr.status !== 'cancelled' && (
                          <select className="status-select" value={pr.status} onChange={(e) => setStatus(pr, e.target.value)}>
                            <option value="pending">Pending</option>
                            <option value="ordered">Ordered</option>
                            <option value="received">Received (add to stock)</option>
                            <option value="cancelled">Cancel</option>
                          </select>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>}
      </div>

      {modal && <PRModal products={products} onClose={() => setModal(false)} onSaved={() => { setModal(false); flash('Purchase request raised'); load(); }} />}
      {approvalFor && <ApprovalModal pr={approvalFor} onClose={() => setApprovalFor(null)} onSaved={() => { setApprovalFor(null); flash('Saved'); load(); }} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function ApprovalModal({ pr, onClose, onSaved }) {
  const [checked, setChecked] = useState(!!pr.checked);
  const [remarks, setRemarks] = useState(pr.remarks || '');
  const [shared, setShared] = useState(!!pr.shared_with_accounts);
  const [sharedDate, setSharedDate] = useState(pr.shared_date ? String(pr.shared_date).slice(0, 10) : new Date().toISOString().slice(0, 10));
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);

  async function save() {
    setError(''); setBusy(true);
    try {
      await api(`/requirements/purchase/${pr.id}/approval`, { method: 'PATCH', body: {
        checked, remarks, shared_with_accounts: shared, shared_date: shared ? sharedDate : null,
      } });
      onSaved();
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Review purchase request</h2>
        <p className="page-desc" style={{ margin: '0 0 16px', fontSize: 13 }}>{pr.product_name} · qty {pr.qty}</p>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer', marginBottom: 14 }}>
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} /> Checked / verified
        </label>

        <div className="field"><label>Remarks</label>
          <input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Any notes on this request" /></div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, cursor: 'pointer', marginTop: 6 }}>
          <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} /> Shared with accounts for fund approval
        </label>
        {shared && (
          <div className="field" style={{ marginTop: 10 }}><label>Date shared with accounts</label>
            <input type="date" value={sharedDate} onChange={(e) => setSharedDate(e.target.value)} /></div>
        )}

        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function PRModal({ products, onClose, onSaved }) {
  const [f, setF] = useState({ product_id: '', qty: '', note: '' });
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function save() {
    setError(''); setBusy(true);
    try { await api('/requirements/purchase', { method: 'POST', body: f }); onSaved(); }
    catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>New purchase request</h2>
        <div className="field"><label>Product</label>
          <select value={f.product_id} autoFocus onChange={set('product_id')}>
            <option value="">Select product…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></div>
        <div className="field"><label>Quantity to purchase</label>
          <input type="number" value={f.qty} onChange={set('qty')} placeholder="0" /></div>
        <div className="field"><label>Note (optional)</label>
          <input value={f.note} onChange={set('note')} placeholder="Supplier, urgency, etc." /></div>
        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy || !f.product_id || !f.qty}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
